import React from 'react';

type ResourceLink = {
  href: string;
  text: string;
};

interface Props {
  copyRightStartDate: string;
  customResourceLinks?: {
    href: string;
    text: string;
  }[];
}

const resourcesLink: ResourceLink[] = [
  {
    href: 'https://www.hey.gg/podcast',
    text: 'Podcast',
  },
  {
    href: 'https://www.youtube.com/@heygoodgames',
    text: 'YouTube',
  },
  {
    href: 'https://www.hey.gg/newsletter',
    text: 'Newsletter',
  },
];

/**
 * @typedef {Object} Props
 * @property {Object[]} [customResourceLinks] - An optional array of custom resource links.
 *   - Each object in the array should have the following properties:
 *     - `href` (string): The URL of the resource link.
 *     - `text` (string): The text to display for the link.
 * @property {string} copyRightStartDate - The starting year for the copyright information.
 */
export default ({ customResourceLinks, copyRightStartDate }: Props) => {
  const currentYear = new Date().getFullYear();
  const uniqueResources = [...resourcesLink]
    .map((link) => {
      const existingResource = customResourceLinks?.find(
        (resource) => resource.text === link.text,
      );
      if (existingResource) {
        return existingResource;
      }
      return link;
    })
    .concat({
      href: 'https://www.hey.gg/privacy',
      text: 'Privacy Policy',
    });

  return (
    <div className="footer">
      <div className="container">
        <div className="aboutus">
          <div className="section">
            <h2>About Us</h2>
            <p>
              We&apos;re <a href="https://www.hey.gg">Hey, Good Game</a>, a tiny game
              studio that builds and buys games, for good. We believe brainy games
              can impact your life in a positive and healthy way, and we&apos;re
              dedicated to creating a premium game experience across platforms.
            </p>
            <p>
              Learn more <a href="https://www.hey.gg/about">about us</a>, and please
              reach out if you have a game to sell. We have a streamlined process
              that treats game caretakers fairly.
            </p>
          </div>
          <p className="copyright">
            © {copyRightStartDate}-{currentYear} Hey Good Game, Inc.
          </p>
        </div>
        <div className="games">
          <h2>Hey, Good Games</h2>
          <ul>
            <li className="mathler">
              <a href="https://www.mathler.com">Mathler</a>
            </li>
            <li className="kakuroconquest">
              <a href="https://www.kakuroconquest.com">Kakuro Conquest</a>
            </li>
            <li className="crosswordle">
              <a href="https://crosswordle.com">Crosswordle</a>
            </li>
            <li className="squeezy">
              <a href="https://imsqueezy.com">Squeezy</a>
            </li>
            <li className="sumplete">
              <a href="https://sumplete.com">Sumplete</a>
            </li>
            <li className="sudokuconquest">
              <a href="https://www.sudokuconquest.com">Sudoku Conquest</a>
            </li>
            <li className="hitoriconquest">
              <a href="https://hitoriconquest.com">Hitori Conquest</a>
            </li>
            <li className="wordga">
              <a href="https://wordga.com">WordGa</a>
            </li>
          </ul>
        </div>
        <div className="resources">
          <h2>Resources</h2>
          <ul>
            {uniqueResources.map((link) => (
              <li key={link.href}>
                <a href={link.href}>{link.text}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
